import { IconButton } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { FullscreenWindow, type WindowEntry } from "@/components/modal";
import { useNoteController } from "@/components/note";
import { HatTextTooltip, HatTooltip } from "@/components/tooltip";
import {
  isActiveSessionStatus,
  sessionSimpleStatus,
  type CheckpointTag,
} from "@/modules/training/public";
import { windowStack } from "@/app/windowStack";
import { SessionStatusChip } from "@/components/status/SessionStatusChip";
import {
  SessionDatasetsView,
  useSessionDatasetsController,
} from "./sessionDatasets/SessionDatasetsView";
import { SessionNameDialog } from "./SessionNameDialog";
import { sessionStartActionText } from "./sessionStartActions";
import { TrainingSessionAnalysisPanel } from "./sessionDetail/analysis/TrainingSessionAnalysisPanel";
import { TrainingSessionCheckpointsPanel } from "./sessionDetail/TrainingSessionDetailPanels";
import {
  TrainingSessionConfigPanel,
  TrainingSessionLogsPanel,
} from "./sessionDetail/TrainingSessionPrimaryPanels";
import { useSessionDiagnostics } from "./sessionDetail/useSessionDiagnostics";
import { useSessionEvalRunSummaries } from "./sessionDetail/useSessionEvalRunSummaries";
import { useSessionMetrics } from "./sessionDetail/useSessionMetrics";
import type {
  TrainingSessionDetailTab,
  TrainingSessionWindowParams,
} from "./trainingSessionWindow";
import { useTrainingSessionDetailController } from "./useTrainingSessionDetailController";
import styles from "./TrainingSessionWindowContent.module.css";

type TrainingSessionWindowContentProps = {
  entry: WindowEntry;
  // 스택에서 이 엔트리가 전체 화면으로 보이는지. false 면 이 컴포넌트는 mount 된 채 남는다. 셸은
  // 본문을 렌더하지 않는다.
  active: boolean;
};

// 학습 곡선의 기본 선택 지표. 논문에서 가장 자주 인용하는 두 손실 곡선이다.
const defaultMetricKeys: readonly string[] = ["training_loss", "validation_loss"];

// 로그를 첫 표면으로 두고, Config 전문·분석 및 평가·체크포인트·입력 Dataset 을 따로 둔다.
//   - 스코프 안 세션은 모두 rich 응답을 받는다. 그래서 체크포인트 탭을 항상 표시한다.
//   - 최소화(active=false)해도 이 컴포넌트는 mount 된 채 남는다. 셸은 본문을 unmount 한다. 그래서
//     이 컴포넌트의 hook 이 가진 상태만 남는다(탭, 이미 읽은 Config·지표).
//   - 지표와 평가 요약은 분석 및 평가 탭과 체크포인트 탭이 공유하는 인스턴스 하나로 받는다.
//     그래서 탭을 바꿔도 요청이 중복되지 않는다.
//   - 입력 Dataset 은 Dataset 탭이 멀티 데이터셋 뷰로 보여준다. 다른 탭에는 입력 목록을 두지 않는다.
//   - 여는 쪽이 focus 를 보내면 그 탭으로 시작한다(Policy 이름 링크 → 체크포인트 탭·해당 행).
//     열린 창에 새 focus 가 오면 보던 탭을 대체하고 그 위치로 이동한다.
export function TrainingSessionWindowContent({
  entry,
  active,
}: TrainingSessionWindowContentProps): ReactNode {
  const { session, owned, focus } = entry.params as TrainingSessionWindowParams;
  const [currentTab, setCurrentTab] = useState<TrainingSessionDetailTab>(focus?.tab ?? "logs");
  // 체크포인트 탭이 강조하고 스크롤할 행. 창이 열려 있는 동안 탭을 바꿔도 유지한다. 그래서
  // 돌아왔을 때 그 행을 다시 찾지 않는다.
  const [focusedCheckpoint, setFocusedCheckpoint] = useState<CheckpointTag | null>(
    focus?.checkpoint ?? null,
  );
  useEffect(() => {
    if (focus === undefined) {
      return;
    }
    setCurrentTab(focus.tab);
    setFocusedCheckpoint(focus.checkpoint ?? null);
  }, [focus]);
  const controller = useTrainingSessionDetailController(session, {
    owned,
    configEnabled: active && currentTab === "config",
    // 창이 보이는 동안 진행 중 세션의 terminal 전이를 받아 live·진단 게이트를 갱신한다.
    statusPollEnabled: active,
  });
  const closeSelf = () => windowStack.close(entry.id);

  const tabs: { value: TrainingSessionDetailTab; label: string }[] = [
    { value: "logs", label: "로그" },
    { value: "config", label: "Config" },
    { value: "analysis", label: "분석 및 Evaluation" },
    { value: "checkpoints", label: "Checkpoint" },
    { value: "datasets", label: "Dataset" },
  ];
  const activeTab = tabs.some((tab) => tab.value === currentTab) ? currentTab : "logs";
  const tabIdPrefix = `${entry.id}-training-session-detail`;
  const panelId = `${tabIdPrefix}-panel`;

  // 연구 노트는 단일 UPSERT 계약을 그대로 쓴다. 에러 메시지 row 저장과는 섞지 않는다.
  const noteController = useNoteController("session", session.id, {
    enabled: active && activeTab === "analysis",
  });

  const [selectedMetricKeys, setSelectedMetricKeys] =
    useState<readonly string[]>(defaultMetricKeys);
  const toggleMetricKey = (key: string) =>
    setSelectedMetricKeys((previous) =>
      previous.includes(key) ? previous.filter((value) => value !== key) : [...previous, key],
    );

  const live = isActiveSessionStatus(controller.effectiveStatus);
  const statusKey = sessionSimpleStatus(controller.effectiveStatus).key;
  const isFailure = statusKey === "failed" || statusKey === "action_required";
  const analysisDataEnabled = active && (activeTab === "analysis" || activeTab === "checkpoints");
  const metrics = useSessionMetrics(session.id, {
    enabled: analysisDataEnabled,
    live,
    selectedKeys: selectedMetricKeys,
  });
  const diagnostics = useSessionDiagnostics(session.id, {
    enabled: active && activeTab === "analysis" && isFailure,
  });
  const evalRunIds = useMemo(
    () =>
      controller.evalState.status === "success"
        ? controller.evalState.data.eval_runs.map((run) => run.eval_run_id)
        : [],
    [controller.evalState],
  );
  const summaries = useSessionEvalRunSummaries(evalRunIds, { enabled: analysisDataEnabled });
  // 입력 목록 상태는 창이 가진다. 탭을 바꾸거나 최소화해도 받은 목록·검색어·선택이 남는다.
  const datasets = useSessionDatasetsController(session.id, {
    enabled: active && activeTab === "datasets",
  });

  // 데이터를 받은 뒤 dock 라벨(제목)을 rich 이름으로 고친다.
  useEffect(() => {
    windowStack.update(entry.id, { title: controller.title });
  }, [entry.id, controller.title]);

  const stepHeader = (
    <div
      className={styles.tabs}
      role="tablist"
      aria-label="Training Session 상세 섹션"
      tabIndex={-1}
      onKeyDown={(event) => handleDetailTabKeyDown(event, tabs, activeTab, setCurrentTab)}
    >
      {tabs.map((tab) => (
        <IconButton
          key={tab.value}
          id={`${tabIdPrefix}-tab-${tab.value}`}
          text={tab.label}
          variant="surface"
          role="tab"
          aria-controls={panelId}
          aria-selected={tab.value === activeTab}
          tabIndex={tab.value === activeTab ? 0 : -1}
          onClick={() => setCurrentTab(tab.value)}
        />
      ))}
    </div>
  );

  const headerActions = (
    <div className={styles.headerActions}>
      <SessionStatusChip status={controller.effectiveStatus}>
        {controller.statusLabel}
      </SessionStatusChip>
      {/* 시작 액션 세 개(복제·이어서 학습·재학습)는 물려받는 것이 서로 다르다. 폭이 좁은 헤더에서는
          hover 툴팁이 그 차이를 표시한다(행 ⋯ 메뉴는 항목 caption 으로 표시한다). */}
      {controller.canClone ? (
        <HatTextTooltip text={sessionStartActionText.clone.hint}>
          <IconButton
            text={
              controller.actionPending === "clone"
                ? "복제하는 중…"
                : sessionStartActionText.clone.label
            }
            size="sm"
            variant="surface"
            disabled={controller.actionPending !== null}
            onClick={controller.openCloneDialog}
          />
        </HatTextTooltip>
      ) : null}
      {controller.canResume ? (
        <HatTextTooltip text={sessionStartActionText.resume.hint}>
          <IconButton
            text={
              controller.actionPending === "resume"
                ? "시작하는 중…"
                : sessionStartActionText.resume.label
            }
            size="sm"
            variant="surface"
            disabled={controller.actionPending !== null}
            onClick={controller.openResumeDialog}
          />
        </HatTextTooltip>
      ) : null}
      {/* 재학습. 체크포인트가 없거나 확인 전이면 사유를 달고 비활성으로 둔다. 숨기면 이 세션으로
          왜 할 수 없는지 알 방법이 없다. 권한이 없을 때는 표시하지 않는다. */}
      {controller.canResubmit ? (
        controller.retrainDisabledReason === null ? (
          <HatTextTooltip text={sessionStartActionText.retrain.hint}>
            <IconButton
              text={sessionStartActionText.retrain.label}
              size="sm"
              variant="surface"
              disabled={controller.actionPending !== null}
              onClick={controller.openRetrain}
            />
          </HatTextTooltip>
        ) : (
          <HatTooltip content={controller.retrainDisabledReason} placement="bottom">
            <span className={styles.disabledActionTrigger}>
              <IconButton
                text={sessionStartActionText.retrain.label}
                size="sm"
                variant="surface"
                disabled
              />
            </span>
          </HatTooltip>
        )
      ) : null}
      {controller.canRetry ? (
        <IconButton
          text={controller.actionPending === "retry" ? "재시도 중…" : "재시도"}
          size="sm"
          variant="surface"
          disabled={controller.actionPending !== null}
          onClick={controller.retry}
        />
      ) : null}
      {controller.canCancel ? (
        <IconButton
          text={controller.actionPending === "cancel" ? "취소 중…" : "취소"}
          size="sm"
          variant="surface"
          disabled={controller.actionPending !== null}
          onClick={controller.cancel}
        />
      ) : null}
    </div>
  );

  return (
    <FullscreenWindow
      open={active}
      title={controller.title}
      stepHeader={stepHeader}
      headerActions={headerActions}
      // Dataset 탭은 목록과 상세가 각자 스크롤한다. 그래서 그 탭에서만 본문이 창 높이를 채운다.
      bodyLayout={activeTab === "datasets" ? "fill" : "scroll"}
      onMinimize={() => windowStack.minimize(entry.id)}
      onBackdrop={() => windowStack.minimize(entry.id)}
      onRequestClose={closeSelf}
    >
      {/* 높이와 스크롤은 셸 bodyLayout 이 소유한다. 이 래퍼는 탭 패널 a11y 를 맡고, 본문을 채우는
          탭에서는 받은 높이를 그대로 내려준다. */}
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={`${tabIdPrefix}-tab-${activeTab}`}
        className={activeTab === "datasets" ? styles.fillPanel : undefined}
      >
        {activeTab === "logs" ? <TrainingSessionLogsPanel controller={controller} /> : null}
        {activeTab === "config" ? <TrainingSessionConfigPanel controller={controller} /> : null}
        {activeTab === "analysis" ? (
          <TrainingSessionAnalysisPanel
            controller={controller}
            noteController={noteController}
            metrics={metrics}
            diagnosticsState={diagnostics.state}
            onReloadDiagnostics={diagnostics.refetch}
            summariesState={summaries.state}
            selectedMetricKeys={selectedMetricKeys}
            onToggleMetricKey={toggleMetricKey}
            live={live}
          />
        ) : null}
        {activeTab === "datasets" ? <SessionDatasetsView controller={datasets} /> : null}
        {activeTab === "checkpoints" ? (
          <TrainingSessionCheckpointsPanel
            active={active}
            controller={controller}
            metrics={metrics}
            summariesState={summaries.state}
            focusedCheckpoint={focusedCheckpoint}
          />
        ) : null}
      </div>
      {controller.resumeDialogOpen ? (
        <SessionNameDialog
          title="Checkpoint에서 이어서 학습합니다."
          hint="마지막 Checkpoint를 이어받는 새 Session을 만듭니다. 이 Session은 그대로 남고, Training 곡선은 두 Session에 걸쳐 이어집니다. 설정을 바꿔야 하면 이어서 학습 대신 새로 제출해 주세요."
          submitLabel={sessionStartActionText.resume.label}
          pendingLabel="시작하는 중…"
          defaultName={controller.newSessionDefaultName}
          submitting={controller.actionPending === "resume"}
          errorMessage={controller.resumeErrorMessage}
          onClose={controller.closeResumeDialog}
          onSubmit={controller.resume}
        />
      ) : null}
      {controller.cloneDialogOpen ? (
        <SessionNameDialog
          title="같은 설정으로 새 Session을 만듭니다."
          hint="이 Session의 시작 조건을 그대로 재현하는 새 Session을 만듭니다. 지금까지의 Training 진행은 이어받지 않습니다. 설정을 바꿔야 하면 복제 대신 새로 제출해 주세요."
          submitLabel={sessionStartActionText.clone.label}
          pendingLabel="복제하는 중…"
          defaultName={controller.newSessionDefaultName}
          submitting={controller.actionPending === "clone"}
          errorMessage={controller.cloneErrorMessage}
          onClose={controller.closeCloneDialog}
          onSubmit={controller.clone}
        />
      ) : null}
    </FullscreenWindow>
  );
}

function handleDetailTabKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
  tabs: readonly { value: TrainingSessionDetailTab; label: string }[],
  activeTab: TrainingSessionDetailTab,
  onSelect: (tab: TrainingSessionDetailTab) => void,
): void {
  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.value === activeTab),
  );
  let nextIndex: number;
  switch (event.key) {
    case "ArrowRight":
      nextIndex = (activeIndex + 1) % tabs.length;
      break;
    case "ArrowLeft":
      nextIndex = (activeIndex - 1 + tabs.length) % tabs.length;
      break;
    case "Home":
      nextIndex = 0;
      break;
    case "End":
      nextIndex = tabs.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('button[role="tab"]');
  buttons[nextIndex]?.focus();
  onSelect(tabs[nextIndex].value);
}
